import { useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { actions as SectionActions } from '@reducers/sections';
import { setSectionOrCourse } from '@utils/cacheCourseAndSection';
import { useSearchParams } from '@utils/hooks/useSearchParams';
import { getGroupActivities } from '@apis/groupActivity';
import { trackPlatformEvent } from '@utils/platformEvents';
import { error as toastError } from '@utils/toast';
import { PLATFORM_EVENT_TYPES } from '../../../../../constants/platformEvents.generated';

export interface BellringerReviewLocationArgs {
  courseId: string | number;
  sectionId: string | number;
  subjectId: string | number;
  unitId: string | number;
  lessonId: string | number;
  groupActivityId: string | number;
  activityLessonId: string | number | undefined;
  pacingGuideId?: string;
}

/** The bellringer review-page location, shared by every path that opens a bellringer. */
export const bellringerReviewLocation = ({
  courseId,
  sectionId,
  subjectId,
  unitId,
  lessonId,
  groupActivityId,
  activityLessonId,
  pacingGuideId,
}: BellringerReviewLocationArgs) => {
  const query: string[] = [];
  if (activityLessonId !== undefined && activityLessonId !== null) {
    query.push(`activityLessonId=${activityLessonId}`);
  }
  if (pacingGuideId) {
    query.push(`pacing-guide-id=${pacingGuideId}`);
  }
  query.push('bellringer=true');
  return {
    pathname: `/lessons/course/${courseId}/section/${sectionId}/subject/${subjectId}/unit/${unitId}/lesson/${lessonId}/online-activity/question/teacher/${groupActivityId}/review`,
    search: `?${query.join('&')}`,
  };
};

export interface LaunchBellringerArgs {
  /** The class actually in the room. Its own group activity is resolved and used. */
  sectionId: number;
  /** The activity's lesson id (stable across sections for the same lesson). */
  activityLessonId: string | number | undefined;
}

/**
 * The single "launch a bellringer for this section" action, shared by the initial
 * intent pop (ActivityFactory) and the inactivity-nudge switch. It always resolves
 * the CHOSEN section's own group activity, so the emitted `bellringer_launched`
 * carries a `group_activity_id` that belongs to `section_id` (they never diverge).
 * It also resets the app's section context (redux + cache) and re-navigates into
 * that section's bellringer. If the section has no online activity for this lesson,
 * it lands on the OLP tab empty state and emits nothing. Resolves to whether a
 * session was launched.
 *
 * The group activity is resolved BEFORE any context change so a failed request
 * cannot leave the app switched to a section it never actually opened.
 */
export const useLaunchBellringerSession = () => {
  const { courseId, subjectId, unitId, lessonId } = useParams<{
    courseId: string;
    subjectId: string;
    unitId: string;
    lessonId: string;
  }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const { 'pacing-guide-id': pacingGuideId } = useSearchParams() as {
    'pacing-guide-id'?: string;
  };

  return useCallback(
    async ({ sectionId, activityLessonId }: LaunchBellringerArgs): Promise<boolean> => {
      // `getGroupActivities` never rejects: on any network/server failure it resolves
      // to `{ error }` with no `response` key. Resolve FIRST so a failure does not
      // silently switch context and land the teacher on the wrong empty tab.
      let result: { response?: { data: Array<{ id: number }> }; error?: unknown };
      try {
        result = (await getGroupActivities(sectionId, lessonId, activityLessonId)) as unknown as {
          response?: { data: Array<{ id: number }> };
          error?: unknown;
        };
      } catch {
        // getGroupActivities is not expected to reject, but guard anyway so an
        // unexpected throw never switches context or leaves the caller hanging.
        toastError('Could not switch classes. Please try again.');
        return false;
      }

      if (!result || result.error || !result.response) {
        toastError('Could not switch classes. Please try again.');
        return false;
      }

      // The fetch succeeded: now it is safe to switch the app's section context.
      dispatch(SectionActions.selectSection(sectionId));
      setSectionOrCourse(sectionId, courseId);

      const groupActivityId = result.response?.data?.[0]?.id;

      if (groupActivityId) {
        trackPlatformEvent(
          PLATFORM_EVENT_TYPES.BELLRINGER_LAUNCHED,
          { sectionId, lessonId },
          {
            group_activity_id: groupActivityId,
            activity_lesson_id: activityLessonId,
            unit_id: unitId,
            ...(pacingGuideId ? { pacing_guide_id: pacingGuideId } : {}),
          },
        );
        history.push(
          bellringerReviewLocation({
            courseId,
            sectionId,
            subjectId,
            unitId,
            lessonId,
            groupActivityId,
            activityLessonId,
            pacingGuideId,
          }),
        );
        return true;
      }

      // The section genuinely has no online activity: land on its OLP tab empty state.
      history.push(
        `/lessons/course/${courseId}/section/${sectionId}/subject/${subjectId}/unit/${unitId}/lesson/${lessonId}?tab=olp`,
      );
      return false;
    },
    [courseId, subjectId, unitId, lessonId, pacingGuideId, dispatch, history],
  );
};
